import { cn } from "@babackapp/ui/lib/utils";
import { Button, Column, Row } from "@react-email/components";
import type { DoubleButtonsBlockProps } from "./types";
import { getThemeButtonStyle } from "../button/shared-button-styling";
import { useEmailThemeRadius } from "../../hooks/use-emails-theme-radius";
import { sectionToParent, useThemeContextualColors } from "../../hooks";
import { DEFAULT_FONT } from "../../utils/constants";

export function DoubleButtonsEmailRenderer({
  block,
  formatter,
  theme,
  locale,
  defaultLocale,
  section,
  isEditor,
}: DoubleButtonsBlockProps & { isEditor?: boolean }) {
  const alignment = block.alignment || "center";
  const font = block.font ?? DEFAULT_FONT;
  const buttonRadius = useEmailThemeRadius(theme, "button");
  const colors = useThemeContextualColors(theme, sectionToParent(section));

  const button0Content =
    block.content?.[locale]?.button0 || block.content?.[defaultLocale]?.button0;
  const button1Content =
    block.content?.[locale]?.button1 || block.content?.[defaultLocale]?.button1;

  const button0Style = getThemeButtonStyle({
    variant: block.properties?.button0Variant || "default",
    theme,
    textColor: colors.text,
  });

  const button1Style = getThemeButtonStyle({
    variant: block.properties?.button1Variant || "outline",
    theme,
    textColor: colors.text,
  });

  let formattedButton0Text =
    !!formatter && button0Content?.text
      ? formatter(button0Content?.text)
      : button0Content?.text;
  let formattedButton1Text =
    !!formatter && button1Content?.text
      ? formatter(button1Content?.text)
      : button1Content?.text;
  let formattedButton0Href =
    !!formatter && button0Content?.href
      ? formatter(button0Content?.href)
      : button0Content?.href;
  let formattedButton1Href =
    !!formatter && button1Content?.href
      ? formatter(button1Content?.href)
      : button1Content?.href;

  if (!formattedButton0Text?.length) {
    if (isEditor) {
      formattedButton0Text = "Ajoutez un label";
    } else {
      return null; // Don't render anything if there's no content and we're not in the editor
    }
  }
  if (!formattedButton1Text?.length) {
    if (isEditor) {
      formattedButton1Text = "Ajoutez un label";
    } else {
      return null; // Don't render anything if there's no content and we're not in the editor
    }
  }
  if (!formattedButton0Href?.length) {
    if (isEditor) {
      formattedButton0Href = "#";
    } else {
      return null; // Don't render anything if there's no content and we're not in the editor
    }
  }
  if (!formattedButton1Href?.length) {
    if (isEditor) {
      formattedButton1Href = "#";
    } else {
      return null; // Don't render anything if there's no content and we're not in the editor
    }
  }

  return (
    <Row>
      <Column
        className={cn({
          "text-left": alignment === "left",
          "text-center": alignment === "center",
          "text-right": alignment === "right",
        })}
      >
        <Button
          href={formattedButton0Href}
          className={cn("inline-block mx-1 no-underline", buttonRadius)}
          style={{ ...button0Style, fontFamily: font } as React.CSSProperties}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: formattedButton0Text,
            }}
          />
        </Button>
        <Button
          href={formattedButton1Href}
          className={cn("inline-block mx-1 no-underline ml-2", buttonRadius)}
          style={{ ...button1Style, fontFamily: font } as React.CSSProperties}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: formattedButton1Text,
            }}
          />
        </Button>
      </Column>
    </Row>
  );
}
